import type { LineStatusResponse } from '@chatapp-inbox/shared';

/**
 * A situação de um número no serviço de mensagens (códigos da Meta) em palavras, para quem administra. Os códigos
 * chegam como texto em maiúsculas; um que não conhecemos aparece como veio (já foi limitado a letras no servidor).
 */

const STATUS: Record<string, string> = {
  CONNECTED: 'Conectado. O número está pronto para enviar e receber mensagens.',
  PENDING:
    'Pendente. O número foi adicionado, mas ainda não foi registrado no WhatsApp: ele não envia nem recebe mensagens até o registro.',
  DISCONNECTED: 'Desconectado. O número não está registrado agora: registre-o de novo.',
  FLAGGED: 'Sinalizado. A qualidade do número caiu e o WhatsApp o está acompanhando.',
  RESTRICTED: 'Restrito. O WhatsApp limitou o envio por este número.',
  BANNED: 'Banido pelo WhatsApp. Não dá para usar este número.',
  MIGRATED: 'Migrado para outra conta do WhatsApp Business.',
  UNVERIFIED: 'Não verificado. Falta confirmar a posse do número (código por SMS ou ligação).',
  DELETED: 'Excluído na Meta.',
};

const NAME: Record<string, string> = {
  APPROVED: 'aprovado',
  AVAILABLE_WITHOUT_REVIEW: 'disponível, sem análise',
  PENDING_REVIEW: 'em análise pela Meta (pode levar de minutos a alguns dias)',
  DECLINED: 'recusado pela Meta (envie outro nome no Gerenciador do WhatsApp)',
  EXPIRED: 'vencido (envie o nome de novo no Gerenciador do WhatsApp)',
  NONE: 'sem nome definido',
};

const CODE: Record<string, string> = {
  VERIFIED: 'concluída',
  NOT_VERIFIED: 'não concluída (peça o código por SMS ou ligação no Gerenciador do WhatsApp)',
  EXPIRED: 'vencida (peça um código novo no Gerenciador do WhatsApp)',
};

const QUALITY: Record<string, string> = {
  GREEN: 'alta',
  YELLOW: 'média',
  RED: 'baixa',
  UNKNOWN: 'ainda não avaliada',
};

export interface LineStatusView {
  /** A frase principal: o estado do número. */
  headline: string;
  /** Linhas de apoio (nome de exibição, verificação do código, qualidade), só as informadas. */
  details: string[];
  /** O registro com PIN pode resolver: mostra o campo do PIN. */
  canRegister: boolean;
  /** O que fazer antes de registrar, se algo impede (código não verificado, nome recusado); `null` = nada. */
  caution: string | null;
}

const word = (table: Record<string, string>, code: string): string => table[code] ?? code;

export function describeLineStatus(status: LineStatusResponse): LineStatusView {
  const details: string[] = [];
  if (status.nameStatus) details.push(`Nome de exibição: ${word(NAME, status.nameStatus)}.`);
  if (status.codeVerification) {
    details.push(
      `Verificação do número (código por SMS ou ligação): ${word(CODE, status.codeVerification)}.`,
    );
  }
  if (status.quality) details.push(`Qualidade: ${word(QUALITY, status.quality)}.`);

  const canRegister = status.status === 'PENDING' || status.status === 'DISCONNECTED';
  let caution: string | null = null;
  if (canRegister) {
    if (status.codeVerification === 'NOT_VERIFIED' || status.codeVerification === 'EXPIRED') {
      caution =
        'Antes de registrar, conclua a verificação do número (código por SMS ou ligação) no Gerenciador do WhatsApp: sem ela a Meta recusa o registro.';
    } else if (status.nameStatus === 'DECLINED') {
      caution =
        'O nome de exibição foi recusado. Envie outro nome no Gerenciador do WhatsApp antes de registrar.';
    } else if (status.nameStatus === 'PENDING_REVIEW') {
      caution =
        'O nome de exibição ainda está em análise. Se o número continuar pendente depois da aprovação, registre-o de novo.';
    }
  }
  return {
    headline: status.status
      ? word(STATUS, status.status)
      : 'A Meta não informou a situação do número.',
    details,
    canRegister,
    caution,
  };
}
